import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Shield, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { apiGetJson, apiSendJson, apiDelete, clientRequestHeaders } from "@/lib/sm-api";
import {
  PLATFORM_ROLE_DESCRIPTIONS,
  PLATFORM_ROLE_LABELS,
  PLATFORM_ROLE_CAPABILITIES,
  PLATFORM_STAFF_ROLES,
  ALL_CAPS,
  PLATFORM_CAPABILITY_LABELS,
  PLATFORM_CAPABILITY_SPECS,
  resolvePlatformCapabilityLevel,
  hasPlatformCapability,
  isPlatformStaffRole,
  platformRoleBadgeClass,
  type PlatformCapability,
  type PlatformStaffRole,
} from "@/lib/platform-staff";
import { CAPABILITY_LEVEL_SHORT_LABELS, type CapabilityLevel } from "@structura/core/operational";
import { useSession } from "@/hooks/use-session";
import { useAppDialogs } from "@/components/ui/app-dialogs";

type StaffRow = {
  id: string;
  email: string;
  name: string;
  role: string;
  tenantId: string | null;
  active: boolean;
  platformRoleTemplateId?: string | null;
  createdAt?: string | null;
  lastAccessAt?: string | null;
};

/** SCRUM-441/442: perfil de plataforma dinámico (mismo patrón que role templates de tenant). */
type PlatformRoleTemplate = {
  id: string;
  name: string;
  description: string | null;
  baseRole: PlatformStaffRole;
  capabilities: PlatformCapability[];
  /** SCRUM-477/478/479: nivel elegido por capability, mismo concepto que el rol de tenant. */
  capabilityLevels?: Record<string, string>;
  isSystemPreset: boolean;
  assignedUsers?: number;
};

export function PlatformStaffPanel() {
  const { user } = useSession();
  const canManage = hasPlatformCapability(user?.role, "platform_staff_manage");
  const [rows, setRows] = useState<StaffRow[]>([]);
  const { confirm } = useAppDialogs();
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<PlatformStaffRole>("support");
  const [saving, setSaving] = useState(false);
  const [tempPw, setTempPw] = useState<{ email: string; password: string } | null>(null);
  const [edit, setEdit] = useState<StaffRow | null>(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editRole, setEditRole] = useState<PlatformStaffRole>("support");
  const [editTemplateId, setEditTemplateId] = useState<string>("");

  // SCRUM-441/442: perfiles de plataforma dinámicos.
  const [templates, setTemplates] = useState<PlatformRoleTemplate[]>([]);
  const [templateEditorOpen, setTemplateEditorOpen] = useState(false);
  const [templateEditing, setTemplateEditing] = useState<PlatformRoleTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState<{
    name: string;
    description: string;
    baseRole: PlatformStaffRole;
    capabilities: PlatformCapability[];
    capabilityLevels: Record<string, CapabilityLevel>;
  }>({ name: "", description: "", baseRole: "support", capabilities: [], capabilityLevels: {} });
  const [savingTemplate, setSavingTemplate] = useState(false);

  // SCRUM-480: "Acceso efectivo" para staff de plataforma — mismo criterio
  // narrativo que ya usa app.equipo.tsx para el tenant, en vez de dejar las
  // capacidades solo como checkboxes en el editor de perfil.
  const [effectiveStaff, setEffectiveStaff] = useState<StaffRow | null>(null);

  const loadTemplates = useCallback(async () => {
    try {
      const r = await apiGetJson<{ templates: PlatformRoleTemplate[] }>("/api/admin/platform-role-templates");
      setTemplates(r.templates ?? []);
    } catch {
      /* aislado — no bloquea el resto del panel */
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all = await apiGetJson<StaffRow[]>("/api/admin/users");
      setRows(all.filter((u) => isPlatformStaffRole(u.role)));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo cargar staff");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    void loadTemplates();
  }, [load, loadTemplates]);

  const sorted = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const rank = (r: string) => (r === "super_admin" ? 0 : r === "support" ? 1 : 2);
        return rank(a.role) - rank(b.role) || a.email.localeCompare(b.email);
      }),
    [rows],
  );

  const createStaff = async () => {
    if (!email.trim()) {
      toast.error("Email obligatorio");
      return;
    }
    setSaving(true);
    try {
      const res = await apiSendJson<{ email: string; tempPassword: string }>("/api/admin/users", "POST", {
        email: email.trim(),
        name: name.trim() || email.split("@")[0],
        role,
        platformStaff: true,
        tenantId: null,
      });
      setTempPw({ email: res.email, password: res.tempPassword });
      setCreateOpen(false);
      setEmail("");
      setName("");
      setRole("support");
      toast.success("Staff creado");
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al crear");
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    if (!edit) return;
    setSaving(true);
    try {
      await apiSendJson("/api/admin/users", "PATCH", {
        userId: edit.id,
        name: editName.trim(),
        email: editEmail.trim(),
        role: editRole,
        tenantId: null,
        platformRoleTemplateId: editTemplateId || null,
      });
      toast.success("Staff actualizado");
      setEdit(null);
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const templatesForRole = (r: PlatformStaffRole) => templates.filter((t) => t.baseRole === r);

  /**
   * SCRUM-480: mismo criterio que `dbSmResolvePlatformCapabilities` en el
   * servidor (server-feature-gate/platform-role-templates.ts) — si el
   * usuario tiene un template asignado Y ese template sigue coincidiendo
   * con su base_role actual, sus capacidades son las del template; si no,
   * cae al fijo PLATFORM_ROLE_CAPABILITIES[role]. Se replica acá solo para
   * MOSTRAR el resultado en la UI — el gate real sigue siendo server-side.
   */
  const effectiveCapabilitiesFor = (u: StaffRow): PlatformCapability[] => {
    if (u.platformRoleTemplateId) {
      const tpl = templates.find((t) => t.id === u.platformRoleTemplateId);
      if (tpl && isPlatformStaffRole(u.role) && tpl.baseRole === u.role) return tpl.capabilities;
    }
    return isPlatformStaffRole(u.role) ? [...PLATFORM_ROLE_CAPABILITIES[u.role]] : [];
  };

  const profileLabelFor = (u: StaffRow): string => {
    if (u.platformRoleTemplateId) {
      const tpl = templates.find((t) => t.id === u.platformRoleTemplateId);
      if (tpl && isPlatformStaffRole(u.role) && tpl.baseRole === u.role) return tpl.name;
    }
    return isPlatformStaffRole(u.role) ? `${PLATFORM_ROLE_LABELS[u.role]} (preset)` : u.role;
  };

  const openTemplateEditor = (tpl: PlatformRoleTemplate | null) => {
    setTemplateEditing(tpl);
    setTemplateForm(
      tpl
        ? {
            name: tpl.name,
            description: tpl.description ?? "",
            baseRole: tpl.baseRole,
            capabilities: [...tpl.capabilities],
            capabilityLevels: { ...(tpl.capabilityLevels as Record<string, CapabilityLevel> | undefined) },
          }
        : { name: "", description: "", baseRole: editRole, capabilities: [], capabilityLevels: {} },
    );
    setTemplateEditorOpen(true);
  };

  const saveTemplate = async () => {
    if (!templateForm.name.trim()) {
      toast.error("Nombre requerido");
      return;
    }
    setSavingTemplate(true);
    try {
      if (templateEditing) {
        await apiSendJson(`/api/admin/platform-role-templates?id=${templateEditing.id}`, "PATCH", {
          name: templateForm.name.trim(),
          description: templateForm.description.trim() || null,
          capabilities: templateForm.capabilities,
          capabilityLevels: templateForm.capabilityLevels,
        });
      } else {
        await apiSendJson("/api/admin/platform-role-templates", "POST", {
          name: templateForm.name.trim(),
          description: templateForm.description.trim() || null,
          baseRole: templateForm.baseRole,
          capabilities: templateForm.capabilities,
          capabilityLevels: templateForm.capabilityLevels,
        });
      }
      toast.success("Perfil guardado");
      setTemplateEditorOpen(false);
      void loadTemplates();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al guardar el perfil");
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async (tpl: PlatformRoleTemplate) => {
    if (!(await confirm({
      title: `¿Eliminar el perfil "${tpl.name}"?`,
      description: "Esta acción no se puede deshacer.",
      destructive: true,
      confirmLabel: "Eliminar",
    }))) return;
    try {
      await apiDelete(`/api/admin/platform-role-templates?id=${tpl.id}`);
      toast.success("Perfil eliminado");
      void loadTemplates();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al eliminar");
    }
  };

  return (
    <div className="space-y-4">
      <div className="glass-card rounded-xl border border-border p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-2xl">
            <h3 className="flex items-center gap-2 font-semibold">
              <Shield className="size-4 text-violet-400" /> Staff de plataforma SaaS
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Usuarios Structura (sin workspace). Distinto del equipo del tenant (/app/equipo) y de la pestaña
              Usuarios (owner/staff de un comercio).
            </p>
            <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
              {PLATFORM_STAFF_ROLES.map((r) => (
                <li key={r}>
                  <span className="font-medium text-foreground">{PLATFORM_ROLE_LABELS[r]}</span>
                  {" — "}
                  {PLATFORM_ROLE_DESCRIPTIONS[r]}
                </li>
              ))}
            </ul>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-2">
              <UserPlus className="size-4" /> Nuevo staff
            </Button>
          )}
        </div>
      </div>

      {!canManage && (
        <p className="text-sm text-amber-600">
          Tu perfil no puede crear ni editar staff. Pedile a un Admin (full) si necesitás un alta.
        </p>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Cargando…
        </div>
      ) : (
        <>
          {/* Cards en mobile */}
          <div className="flex flex-col gap-2 md:hidden">
            {sorted.map((u) => (
              <div key={u.id} className="rounded-xl border border-border p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-sm">{u.name || "—"}</p>
                    <p className="text-xs text-muted-foreground break-all">{u.email}</p>
                  </div>
                  <Badge variant="outline" className={`shrink-0 ${platformRoleBadgeClass(u.role)}`}>
                    {isPlatformStaffRole(u.role) ? PLATFORM_ROLE_LABELS[u.role] : u.role}
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Activo: {u.active === false ? "No" : "Sí"}
                </p>
                {canManage && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="text-xs"
                      onClick={() => {
                        setEdit(u);
                        setEditName(u.name);
                        setEditEmail(u.email);
                        setEditRole(u.role as PlatformStaffRole);
                        setEditTemplateId(u.platformRoleTemplateId ?? "");
                      }}
                    >
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs"
                      disabled={u.role === "super_admin"}
                      onClick={() => {
                        void (async () => {
                          try {
                            const res = await apiSendJson<{ tempPassword: string }>(
                              "/api/admin/set-password",
                              "POST",
                              { userId: u.id },
                            );
                            setTempPw({ email: u.email, password: res.tempPassword });
                          } catch (e) {
                            toast.error(e instanceof Error ? e.message : "Error");
                          }
                        })();
                      }}
                    >
                      Contraseña temporal
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs"
                      onClick={() => {
                        void (async () => {
                          const on = u.active !== false;
                          try {
                            await apiSendJson("/api/admin/users", "PATCH", {
                              userId: u.id,
                              active: !on,
                            });
                            toast.success(on ? "Desactivado" : "Activado");
                            void load();
                          } catch (e) {
                            toast.error(e instanceof Error ? e.message : "Error");
                          }
                        })();
                      }}
                    >
                      {u.active === false ? "Activar" : "Desactivar"}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="text-xs"
                      disabled={u.role === "super_admin"}
                      onClick={() => {
                        void (async () => {
                          if (!(await confirm({ title: `¿Eliminar staff ${u.email}?`, destructive: true, confirmLabel: "Eliminar" }))) return;
                          try {
                            await fetch(`/api/admin/users?id=${encodeURIComponent(u.id)}`, {
                              method: "DELETE",
                              credentials: "include",
                              headers: clientRequestHeaders(),
                            });
                            toast.success("Eliminado");
                            void load();
                          } catch {
                            toast.error("No se pudo eliminar");
                          }
                        })();
                      }}
                    >
                      Eliminar
                    </Button>
                  </div>
                )}
              </div>
            ))}
            {sorted.length === 0 && (
              <p className="text-center text-sm text-muted-foreground">Todavía no hay staff de plataforma listado.</p>
            )}
          </div>

          {/* Tabla en desktop */}
          <div className="hidden overflow-x-auto rounded-xl border border-border md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Perfil</TableHead>
                <TableHead>Activo</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="font-medium">{u.name || "—"}</TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={platformRoleBadgeClass(u.role)}>
                      {isPlatformStaffRole(u.role) ? PLATFORM_ROLE_LABELS[u.role] : u.role}
                    </Badge>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">{profileLabelFor(u)}</p>
                  </TableCell>
                  <TableCell className="text-xs">{u.active === false ? "No" : "Sí"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-wrap justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs"
                        onClick={() => setEffectiveStaff(u)}
                      >
                        Ver acceso
                      </Button>
                      {canManage && (
                        <>
                          <Button
                            size="sm"
                            variant="secondary"
                            className="text-xs"
                            onClick={() => {
                              setEdit(u);
                              setEditName(u.name);
                              setEditEmail(u.email);
                              setEditRole(u.role as PlatformStaffRole);
                              setEditTemplateId(u.platformRoleTemplateId ?? "");
                            }}
                          >
                            Editar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            disabled={u.role === "super_admin"}
                            onClick={() => {
                              void (async () => {
                                try {
                                  const res = await apiSendJson<{ tempPassword: string }>(
                                    "/api/admin/set-password",
                                    "POST",
                                    { userId: u.id },
                                  );
                                  setTempPw({ email: u.email, password: res.tempPassword });
                                } catch (e) {
                                  toast.error(e instanceof Error ? e.message : "Error");
                                }
                              })();
                            }}
                          >
                            Contraseña temporal
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            onClick={() => {
                              void (async () => {
                                const on = u.active !== false;
                                try {
                                  await apiSendJson("/api/admin/users", "PATCH", {
                                    userId: u.id,
                                    active: !on,
                                  });
                                  toast.success(on ? "Desactivado" : "Activado");
                                  void load();
                                } catch (e) {
                                  toast.error(e instanceof Error ? e.message : "Error");
                                }
                              })();
                            }}
                          >
                            {u.active === false ? "Activar" : "Desactivar"}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="text-xs"
                            disabled={u.role === "super_admin"}
                            onClick={() => {
                              void (async () => {
                                if (!(await confirm({ title: `¿Eliminar staff ${u.email}?`, destructive: true, confirmLabel: "Eliminar" }))) return;
                                try {
                                  await fetch(`/api/admin/users?id=${encodeURIComponent(u.id)}`, {
                                    method: "DELETE",
                                    credentials: "include",
                                    headers: clientRequestHeaders(),
                                  });
                                  toast.success("Eliminado");
                                  void load();
                                } catch {
                                  toast.error("No se pudo eliminar");
                                }
                              })();
                            }}
                          >
                            Eliminar
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {sorted.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                    Todavía no hay staff de plataforma listado.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </div>
        </>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo staff de plataforma</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ops@structura.com.ar" />
            </div>
            <div className="space-y-1">
              <Label>Nombre</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre" />
            </div>
            <div className="space-y-1">
              <Label>Perfil</Label>
              <Select value={role} onValueChange={(v) => setRole(v as PlatformStaffRole)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLATFORM_STAFF_ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {PLATFORM_ROLE_LABELS[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{PLATFORM_ROLE_DESCRIPTIONS[role]}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancelar
            </Button>
            <Button disabled={saving} onClick={() => void createStaff()}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar staff</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Email</Label>
              <Input value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Nombre</Label>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Nivel</Label>
              <Select
                value={editRole}
                onValueChange={(v) => {
                  setEditRole(v as PlatformStaffRole);
                  setEditTemplateId("");
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLATFORM_STAFF_ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {PLATFORM_ROLE_LABELS[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label>Perfil específico (opcional)</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  onClick={() => openTemplateEditor(null)}
                >
                  + Crear perfil
                </Button>
              </div>
              <Select value={editTemplateId || "__default"} onValueChange={(v) => setEditTemplateId(v === "__default" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__default">Predeterminado de {PLATFORM_ROLE_LABELS[editRole]}</SelectItem>
                  {templatesForRole(editRole).map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} {t.isSystemPreset ? "" : "(personalizado)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editTemplateId && !templatesForRole(editRole).find((t) => t.id === editTemplateId)?.isSystemPreset ? (
                <button
                  type="button"
                  className="text-xs text-muted-foreground underline"
                  onClick={() => {
                    const tpl = templates.find((t) => t.id === editTemplateId);
                    if (tpl) openTemplateEditor(tpl);
                  }}
                >
                  Editar este perfil
                </button>
              ) : null}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>
              Cancelar
            </Button>
            <Button disabled={saving} onClick={() => void saveEdit()}>
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tempPw} onOpenChange={(o) => !o && setTempPw(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Contraseña temporal</DialogTitle>
          </DialogHeader>
          {tempPw && (
            <div className="space-y-2 text-sm">
              <p>
                Usuario: <span className="font-medium">{tempPw.email}</span>
              </p>
              <p className="rounded-md border border-border bg-muted/40 px-3 py-2 font-mono text-base tracking-wide">
                {tempPw.password}
              </p>
              <p className="text-xs text-muted-foreground">Copiá y compartí por un canal seguro. No se vuelve a mostrar.</p>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={() => {
                if (tempPw) void navigator.clipboard.writeText(tempPw.password);
                toast.success("Copiada");
              }}
            >
              Copiar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SCRUM-480: Acceso efectivo — narrativo, no una grilla de checkboxes suelta. */}
      <Dialog open={!!effectiveStaff} onOpenChange={(o) => !o && setEffectiveStaff(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {effectiveStaff ? `Acceso efectivo · ${effectiveStaff.name || effectiveStaff.email}` : "Acceso efectivo"}
            </DialogTitle>
          </DialogHeader>
          {effectiveStaff && (
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">
                Perfil aplicado: <span className="font-medium text-foreground">{profileLabelFor(effectiveStaff)}</span>
              </p>
              <ul className="space-y-1.5">
                {ALL_CAPS.map((cap) => {
                  const granted = effectiveCapabilitiesFor(effectiveStaff).includes(cap);
                  const tpl = effectiveStaff.platformRoleTemplateId
                    ? templates.find((t) => t.id === effectiveStaff.platformRoleTemplateId)
                    : undefined;
                  const rawLevel = tpl?.capabilityLevels?.[cap];
                  const templateLevel = rawLevel === "read" || rawLevel === "write" || rawLevel === "admin" ? rawLevel : null;
                  const level = resolvePlatformCapabilityLevel(cap, {
                    templateHasCapability: granted,
                    templateLevel,
                  });
                  return (
                    <li
                      key={cap}
                      className={granted ? "text-foreground" : "text-muted-foreground line-through"}
                    >
                      {granted ? "✓" : "—"} {PLATFORM_CAPABILITY_LABELS[cap]}
                      {granted && PLATFORM_CAPABILITY_SPECS[cap].supportsSubLevels && (
                        <span className="ml-1.5 text-[10px] text-muted-foreground">
                          ({CAPABILITY_LEVEL_SHORT_LABELS[level]})
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
              <p className="text-xs text-muted-foreground">
                Esto refleja el mismo criterio que aplica el servidor (dbSmResolvePlatformCapabilitiesWithLevels) —
                es solo de lectura acá; el gate real corre en cada endpoint.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEffectiveStaff(null)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={templateEditorOpen} onOpenChange={setTemplateEditorOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{templateEditing ? `Editar perfil · ${templateEditing.name}` : "Crear perfil"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {!templateEditing ? (
              <div className="space-y-1">
                <Label>Nivel base</Label>
                <Select
                  value={templateForm.baseRole}
                  onValueChange={(v) => setTemplateForm((f) => ({ ...f, baseRole: v as PlatformStaffRole }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLATFORM_STAFF_ROLES.map((r) => (
                      <SelectItem key={r} value={r}>
                        {PLATFORM_ROLE_LABELS[r]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div className="space-y-1">
              <Label>Nombre</Label>
              <Input
                value={templateForm.name}
                onChange={(e) => setTemplateForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Ej. Soporte Senior"
              />
            </div>
            <div className="space-y-1">
              <Label>Descripción</Label>
              <Input
                value={templateForm.description}
                onChange={(e) => setTemplateForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">Capacidades</p>
              <p className="text-xs text-muted-foreground">
                Mismo concepto que los roles de tenant: para la mayoría alcanza con activarlo o no; donde
                tiene sentido podés elegir el nivel (lectura, edición, control total).
              </p>
              <div className="space-y-1 rounded-md border border-border p-2">
                {ALL_CAPS.map((cap) => {
                  const spec = PLATFORM_CAPABILITY_SPECS[cap];
                  const checked = templateForm.capabilities.includes(cap);
                  const selectedLevel = templateForm.capabilityLevels[cap];
                  const displayLevel: CapabilityLevel = selectedLevel ?? (checked ? spec.booleanEquivalentLevel : "none");

                  const handleLevelChange = (level: CapabilityLevel) => {
                    setTemplateForm((f) => {
                      const capabilities =
                        level === "none"
                          ? f.capabilities.filter((c) => c !== cap)
                          : f.capabilities.includes(cap)
                            ? f.capabilities
                            : [...f.capabilities, cap];
                      const capabilityLevels = { ...f.capabilityLevels };
                      if (level === "none") delete capabilityLevels[cap];
                      else capabilityLevels[cap] = level;
                      return { ...f, capabilities, capabilityLevels };
                    });
                  };

                  if (spec.supportsSubLevels) {
                    return (
                      <div key={cap} className="flex items-center gap-2 text-sm">
                        <span className="flex-1">{PLATFORM_CAPABILITY_LABELS[cap]}</span>
                        <select
                          value={displayLevel}
                          onChange={(e) => handleLevelChange(e.target.value as CapabilityLevel)}
                          className="h-7 rounded border border-input bg-transparent px-1.5 text-[11px] text-muted-foreground"
                          data-testid={`platform-capability-level-select-${cap}`}
                        >
                          {(["none", "read", "write", "admin"] as CapabilityLevel[]).map((lvl) => (
                            <option key={lvl} value={lvl}>
                              {CAPABILITY_LEVEL_SHORT_LABELS[lvl]}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  }

                  return (
                    <label key={cap} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) =>
                          setTemplateForm((f) => ({
                            ...f,
                            capabilities: e.target.checked
                              ? [...f.capabilities, cap]
                              : f.capabilities.filter((c) => c !== cap),
                          }))
                        }
                      />
                      {PLATFORM_CAPABILITY_LABELS[cap]}
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter className="justify-between sm:justify-between">
            {templateEditing ? (
              <Button
                type="button"
                variant="outline"
                className="text-destructive"
                onClick={() => void deleteTemplate(templateEditing).then(() => setTemplateEditorOpen(false))}
              >
                Eliminar
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setTemplateEditorOpen(false)}>
                Cancelar
              </Button>
              <Button disabled={savingTemplate} onClick={() => void saveTemplate()}>
                Guardar
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
